import { RedirectWithSearch } from '@/components/RedirectWithSearch';
import { TrackOnLeave } from '@/components/TrackOnLeave';
import { FormTitleAndLogo } from '@/pages/components/Form';
import { FormElements } from '@/pages/components/form-fields/FormType';
import { LocationInput } from '@/pages/components/form-fields/input/LocationInput';
import PhoneNumber from '@/pages/components/form-fields/phoneNumber/PhoneNumber';
import {
  ConfirmMobileNumberDialog,
  requestMobileNumberConfirmation,
} from '@/pages/DirectToDoctorForms/ConfirmMobileNumberDialog';
import { DirectToDoctorConsultEligible } from '@/pages/DirectToDoctorForms/DirectToDoctorConsultEligible';
import { MedicareExpiryField, MedicareNumberField } from '@/pages/DirectToDoctorForms/MedicareFields';
import type { Step } from '@montugroup/form-builder';

export const MOBILE_NUMBER_FIELD = 'mobileNumber.phoneNo';

export const directToDoctorSteps = ({ onStart }: { onStart: () => void }): Step[] => [
  {
    id: 1,
    type: 'section',
    route: '',
    formTitle: (
      <FormTitleAndLogo>
        Thank you. You can go straight to seeing a doctor. We just need a few details first.
      </FormTitleAndLogo>
    ),
    nextButtonConfig: {
      text: 'Start filling out my details',
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'start',
      },
    },
    content: [
      {
        type: 'custom',
        component: <DirectToDoctorConsultEligible />,
      },
      {
        type: 'custom',
        component: <TrackOnLeave onLeave={onStart} />,
      },
    ],
  } as Step,
  {
    id: 2,
    type: 'question',
    route: 'mobile-number',
    qid: 'mobileNumber',
    formTitle: <FormTitleAndLogo>Personal details</FormTitleAndLogo>,
    title: 'What is your mobile number?*',
    subtitle: "We'll SMS a unique link to this number to join your video consult with your doctor.",
    nextButtonConfig: {
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'mobile-number',
      },
    },
    content: [
      {
        type: 'custom',
        name: 'phoneNo',
        component: (
          <PhoneNumber
            name={MOBILE_NUMBER_FIELD}
            type={FormElements.phoneNumber}
            label="Mobile number"
            countrySelect
            requiredText="Mobile number required"
            invalidNumberText="Please enter a valid mobile phone number."
          />
        ),
      },
      {
        type: 'custom',
        name: 'confirmMobileNumberDialog',
        component: <ConfirmMobileNumberDialog />,
      },
    ],

    skipTo: (nextStepIndex) => {
      requestMobileNumberConfirmation();
      return nextStepIndex - 1;
    },
  } as Step,
  {
    id: 3,
    type: 'question',
    route: 'name',
    qid: 'fullName',
    formTitle: <FormTitleAndLogo>Personal details</FormTitleAndLogo>,
    title: 'What is your first name and surname, as it appears on your official documentation?*',
    subtitle: 'Please do **not** include middle names',
    enableNextOnlyWhenValid: true,
    nextButtonConfig: {
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'full-name',
      },
    },
    fieldArray: true,
    content: [
      { name: 'firstName', type: 'input', label: 'First name*' },
      { name: 'lastName', type: 'input', label: 'Surname*' },
    ],
  } as Step,
  {
    id: 4,
    type: 'question',
    route: 'date-of-birth',
    qid: 'birthday',
    formTitle: <FormTitleAndLogo>Personal details</FormTitleAndLogo>,
    title: 'What is your date of birth?*',
    subtitle: 'This is used to verify your identity and link your records with your IHI number.',
    nextButtonConfig: {
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'dob',
      },
    },
    content: [
      {
        type: 'date',
        name: 'birthday',
        label: 'Date of Birth',
        legalAge: true,
      },
    ],
  } as Step,
  {
    id: 5,
    type: 'question',
    route: 'address',
    qid: 'address',
    formTitle: <FormTitleAndLogo>Personal details</FormTitleAndLogo>,
    title: 'What is your home address?*',
    subtitle: 'This is used to verify your identity and link your records with your IHI number.',
    enableNextOnlyWhenValid: true,
    nextButtonConfig: {
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'home-address',
      },
    },
    fieldArray: true,
    content: [
      {
        type: 'custom',
        name: 'streetAndHouseNo',
        component: <LocationInput name="address.streetAndHouseNo" label="Street and house number" />,
      },
      {
        name: 'suburb',
        type: 'input',
        label: 'Suburb',
      },
      {
        name: 'state',
        type: 'select',
        label: 'State',
        options: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
      },
      {
        name: 'postcode',
        type: 'number',
        label: 'Postcode',
        inputProps: {
          format: '####',
        },
      },
    ],
  } as Step,
  {
    id: 6,
    type: 'question',
    route: 'emergency-contact',
    qid: 'emergencyContact',
    formTitle: <FormTitleAndLogo>Personal details</FormTitleAndLogo>,
    title: 'Who can we contact in case of an emergency?*',
    subtitle: 'We will only use this information in cases of emergency',
    enableNextOnlyWhenValid: true,
    nextButtonConfig: {
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'emergency-contact',
      },
    },
    fieldArray: true,
    content: [
      { name: 'firstName', type: 'input', label: 'First name' },
      { name: 'lastName', type: 'input', label: 'Last name' },
      {
        name: 'relationship',
        type: 'select',
        label: 'Relationship',
        options: ['Parent', 'Partner', 'Sibling', 'Friend', 'Other'],
      },
      {
        type: 'custom',
        name: 'phoneNo',
        component: (
          <PhoneNumber
            name="emergencyContact.phoneNo"
            type={FormElements.phoneNumber}
            label="Phone Number"
            countrySelect
            requiredText="Phone number required"
            invalidNumberText="Please enter a valid mobile phone number or landline number."
            mustNotMatch={[MOBILE_NUMBER_FIELD, 'Emergency contact number must be different to your own phone number.']}
          />
        ),
      },
    ],
  } as Step,
  {
    id: 7,
    type: 'question',
    route: 'medicare',
    qid: 'medicare',
    formTitle: <FormTitleAndLogo>Personal details</FormTitleAndLogo>,
    title: 'What are your Medicare card details?*',
    subtitle:
      'This is used to verify your identity and link your records with your IHI number. \n\n ![](/assets/medicare.png)',
    enableNextOnlyWhenValid: true,
    nextButtonConfig: {
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'medicare-details',
      },
    },
    fieldArray: true,
    content: [
      {
        type: 'custom',
        name: 'number',
        component: <MedicareNumberField />,
      },
      {
        name: 'referenceNo',
        type: 'number',
        label: '2. Medicare reference number',
        helperText: 'The single digit number along the left side',
        inputProps: { format: '#' },
      },
      {
        name: 'fullName',
        type: 'input',
        label: '3. Name as it appears on your card',
      },
      {
        type: 'custom',
        name: 'expiryDate',
        component: <MedicareExpiryField />,
      },
    ],
  } as Step,
  {
    id: 8,
    type: 'question',
    route: 'gender',
    qid: 'gender',
    formTitle: <FormTitleAndLogo>Personal details</FormTitleAndLogo>,
    title: 'What is your gender, as it appears on your official documentation?*',
    subtitle: 'This is used to verify your identity and link your records with your IHI number.',
    nextButtonConfig: {
      // TODO: Placeholder content - Confirm metadata id
      metadata: {
        id: 'gender',
      },
    },
    content: [
      {
        type: 'text',
        value: 'Select gender*',
      },
      {
        type: 'radio',
        values: [
          {
            id: 1,
            label: 'Female',
            value: 'Female',
          },
          {
            id: 2,
            label: 'Male',
            value: 'Male',
          },
          {
            id: 3,
            label: 'Non-binary',
            value: 'Non-binary',
          },
        ],
      },
    ],
  } as Step,
  {
    id: 9,
    type: 'section',
    // The redirect waits for the save, so a failed submission leaves the
    // patient on the error rather than moving them on.
    shouldSave: true,
    hideBackButton: true,
    nextButtonConfig: {
      text: '',
    },
    content: [
      {
        type: 'custom',
        component: <RedirectWithSearch to="/consult-eligible-doctor" />,
      },
    ],
  } as Step,
];
